import type { CoachJourneyInput } from '../schema';

/**
 * The onboarding for buyers from Simon Squibb's World's Biggest Business Competition, as a
 * guided journey: three questions answered on placeholder cards (by voice, a document or
 * text), Simon's top posts and the ready-to-post pictures from Lance's template board, a chat
 * that already holds the prompt, and the carousel it makes. Started by link only
 * (`/new-onboarding?coach=wbbc-post`), for WBBC buyers. The same tour as the board tour's
 * `wbbc-challenge` (PR #2504), built from coach blocks.
 */

/** What answers a question: a recording, a written note, or a document. */
const ANSWER_TYPES = ['audioNode', 'textNode', 'documentNode'] as const;

/** One question on its placeholder card: the card points at it; what's added fills it. */
function question(input: {
  id: string;
  slot: string;
  answer: string;
  title: string;
  body: string;
  next: string;
  skippable: boolean;
}): CoachJourneyInput['steps'][number] {
  return {
    id: input.id,
    title: input.title,
    body: input.body,
    // As the board tour frames a question: just its card, centered, the card on its right.
    show: [{ node: input.slot, placement: 'right', spotlight: false }],
    focus: { node: input.slot, padding: 1, maxZoom: 1 },
    form: { id: 'content', bind: input.answer, accept: [...ANSWER_TYPES] },
    do: {
      action: 'board.fill-slot',
      params: { slot: input.slot, node: input.answer },
      then: input.next,
    },
    ...(input.skippable
      ? { action: { label: 'Skip for now', to: input.next, placement: 'target' as const } }
      : {}),
  };
}

const CAROUSEL_PROMPT =
  'Make me a 5-slide Instagram carousel about why I personally signed up for the World’s Biggest Business Competition and why other people should join too, including what they get. Use my answers for my story and how I talk, and Simon’s top posts and pictures for what works. Slide 1 is a hook that stops the scroll. Slides 2 to 4 tell my story and what the challenge is. The last slide is what they get and a clear call to join. Then write the caption to go with it.';

const SEND_COPY = {
  title: 'Hit Send and your carousel gets made ✨',
  body: "It takes a couple of minutes. I've already written the prompt with your answers and Simon's best posts.",
};

/** The board tour's layout: question cards in a column, the chat to their right, the groups left. */
const SLOT = { width: 300, height: 200 };
const GROUP_X = -1760;

export const wbbcPostJourney: CoachJourneyInput = {
  id: 'wbbc-post',
  version: 2,
  name: 'WBBC Challenge Post',
  description:
    'Tell your story, get a caption, and post for the World’s Biggest Business Competition.',
  mode: 'guided',
  dismissible: false,
  gate: { flag: 'wbbcCustomer' },
  start: { link: true },
  tutorial: {
    description: 'Tell your story and turn it into a carousel for the WBBC.',
    on: 'new-board',
  },
  steps: [
    {
      id: 'open',
      display: 'hidden',
      title: 'Open a board.',
      body: 'Open a board you can edit.',
      on: [{ event: 'board.entered', match: ['boardId'], bind: ['boardId'], to: 'layout' }],
    },
    {
      id: 'layout',
      display: 'hidden',
      title: 'Laying out the board.',
      body: 'Laying out the board.',
      do: [
        {
          action: 'board.add-slot',
          params: { title: '1. About You', note: 'About You', at: { x: 0, y: 100 }, size: SLOT },
          bind: { nodeId: 'aboutSlot' },
        },
        {
          action: 'board.add-slot',
          params: {
            title: '2. Why You Joined',
            note: 'Why You Joined',
            at: { x: 0, y: 400 },
            size: SLOT,
          },
          bind: { nodeId: 'whySlot' },
        },
        {
          action: 'board.add-slot',
          params: {
            title: '3. Why Others Should Join',
            note: 'Why Others Should Join',
            at: { x: 0, y: 700 },
            size: SLOT,
          },
          bind: { nodeId: 'joinSlot' },
        },
        {
          action: 'board.add-chat',
          params: { at: { x: 750, y: 100 }, hidden: true },
          bind: { chatNodeId: 'chatNodeId' },
        },
        {
          action: 'board.add-group',
          params: { group: 'wbbc-top-posts', at: { x: GROUP_X, y: 100 }, hidden: true },
          bind: { groupNodeId: 'postsNodeId' },
        },
        {
          action: 'board.add-group',
          params: { group: 'wbbc-ready-pictures', at: { x: GROUP_X * 2, y: 100 }, hidden: true },
          bind: { groupNodeId: 'picturesNodeId' },
          then: 'about',
        },
      ],
    },
    question({
      id: 'about',
      slot: 'aboutSlot',
      answer: 'aboutNodeId',
      title: 'First up, tell me about YOU 🎙️',
      body: 'Give me a little personal background. Who are you and what do you do?\n\nHit record and just talk, type it out, or drop in a doc. Whatever feels easiest!',
      next: 'why',
      skippable: true,
    }),
    question({
      id: 'why',
      slot: 'whySlot',
      answer: 'whyNodeId',
      title: 'Why did you sign up for the challenge? 🏆',
      body: 'Tell me what made you jump in. The real reason, not the polished one 😉\n\nTalk, type or upload. Your call!',
      next: 'join',
      skippable: true,
    }),
    question({
      id: 'join',
      slot: 'joinSlot',
      answer: 'joinNodeId',
      title: 'Why should your people join the WBBC? 🚀',
      body: 'What would you tell a friend? And what do they get when they sign up?\n\nThis is the bit that gets them clicking 👀',
      next: 'show-posts',
      skippable: false,
    }),
    {
      id: 'show-posts',
      display: 'hidden',
      title: 'Showing the posts.',
      body: 'Showing the posts.',
      // The chat appears with the first group, so it is there when the tour gets to it.
      do: {
        action: 'board.reveal',
        params: { nodes: ['postsNodeId', 'chatNodeId'] },
        then: 'posts',
      },
    },
    {
      id: 'posts',
      title: "Here's what's working for Simon right now 🔥",
      body: "These are Simon's 10 biggest posts. They show what's working right now.\n\nYour carousel will use them, and your answers, for its story ✨",
      show: [{ node: 'postsNodeId', placement: 'right' }],
      focus: { node: 'postsNodeId', placement: 'right', padding: 0.15 },
      action: { label: 'Next Step', to: 'show-pictures' },
    },
    {
      id: 'show-pictures',
      display: 'hidden',
      title: 'Showing the pictures.',
      body: 'Showing the pictures.',
      do: { action: 'board.reveal', params: { nodes: ['picturesNodeId'] }, then: 'pictures' },
    },
    {
      id: 'pictures',
      title: 'Pictures ready to post 📸',
      body: 'These are ready to post. Your carousel can use them as its pictures 📸',
      show: [{ node: 'picturesNodeId', placement: 'right' }],
      focus: { node: 'picturesNodeId', placement: 'right', padding: 0.15 },
      action: { label: 'Next Step', to: 'brand' },
    },
    {
      id: 'brand',
      title: 'Poppy already sounds like you!',
      body: "I've picked your latest brand, so everything Poppy writes uses your voice. Click it any time to switch brands.",
      when: { brand: 'chosen' },
      otherwise: 'prompt',
      // Beside the pill, which sits at the chat's bottom left; the chat fills the screen.
      show: [{ anchor: 'chat.brand', placement: 'left' }],
      focus: { node: 'chatNodeId', placement: 'left', padding: 0.1 },
      action: { label: 'Got it!', to: 'prompt' },
    },
    {
      id: 'prompt',
      display: 'hidden',
      title: 'Writing the prompt.',
      body: 'Writing the prompt.',
      do: [
        // Unanswered cards go, and everything that was answered is wired into the chat.
        { action: 'board.remove', params: { nodes: ['aboutSlot', 'whySlot', 'joinSlot'] } },
        {
          action: 'board.connect',
          params: {
            nodes: ['aboutNodeId', 'whyNodeId', 'joinNodeId', 'postsNodeId', 'picturesNodeId'],
            chat: 'chatNodeId',
          },
        },
        {
          action: 'chat.fill',
          params: { chat: 'chatNodeId', prompt: CAROUSEL_PROMPT },
          then: 'reading',
        },
      ],
    },
    {
      id: 'reading',
      title: "Hang tight... I'm reading through your content!",
      body: 'The Send button unlocks the moment everything you added is ready to chat about. You can improve the prompt while you wait if you need to!',
      show: [{ node: 'chatNodeId', placement: 'right' }],
      focus: { node: 'chatNodeId', placement: 'right', padding: 0.2 },
      waitFor: { nodes: ['aboutNodeId', 'whyNodeId', 'joinNodeId'], to: 'send', atMost: 90 },
      on: [{ event: 'chat.sent', match: ['boardId', 'chatNodeId'], to: 'making' }],
    },
    {
      id: 'send',
      title: SEND_COPY.title,
      body: SEND_COPY.body,
      show: [{ node: 'chatNodeId', placement: 'right' }],
      focus: { node: 'chatNodeId', placement: 'right', padding: 0.2 },
      on: [{ event: 'chat.sent', match: ['boardId', 'chatNodeId'], to: 'making' }],
    },
    {
      id: 'making',
      display: 'hidden',
      title: 'Making the carousel.',
      body: 'Making the carousel.',
      resumeAt: 'send',
      on: [
        {
          event: 'carousel.created',
          match: ['boardId', 'chatNodeId'],
          bindAs: { nodeId: 'carouselNodeId' },
          fresh: true,
          to: 'carousel',
        },
        // The reply ended without a carousel: back to Send, to ask again.
        { event: 'chat.replied', match: ['boardId', 'chatNodeId'], to: 'send' },
      ],
    },
    {
      id: 'carousel',
      title: 'This is your carousel! 🎠',
      body: 'It just landed on the right of your chat.\n\nYou can post it to social media from here 🚀',
      show: [{ node: 'carouselNodeId', placement: 'bottom' }],
      action: { label: 'Done! 🎉', to: 'finish' },
    },
    {
      id: 'finish',
      display: 'hidden',
      title: 'Done.',
      body: 'Done.',
      do: [
        { action: 'app.celebrate' },
        { action: 'app.survey', params: { survey: 'onboarding-tour' }, then: 'end' },
      ],
    },
  ],
};
